import { useState, useEffect, useCallback } from "react";
import { NewConnectionFeesResponse } from "../types";
import { apiClientPublic } from "src/api/apiService";

interface UseNewConnectionFeesOptions {
  /**
   * Whether to fetch data automatically when the hook is mounted
   */
  autoFetch?: boolean;
  /**
   * Request ID to fetch fees for (required if autoFetch is true)
   */
  requestId?: string;
  /**
   * Callback function called when data is successfully fetched
   */
  onSuccess?: (data: NewConnectionFeesResponse) => void;
  /**
   * Callback function called when an error occurs
   */
  onError?: (error: Error) => void;
}

interface UseNewConnectionFeesResult {
  /**
   * The fetched connection fees data
   */
  data: NewConnectionFeesResponse | null;
  /**
   * Loading state indicator
   */
  loading: boolean;
  /**
   * Error state, null if no error
   */
  error: Error | null;
  /**
   * Function to manually fetch fees for a given request ID
   */
  fetchFees: (requestId: string) => Promise<void>;
  /**
   * Function to reset the hook state
   */
  reset: () => void;
  /**
   * Function to retry the last failed request
   */
  retry: () => Promise<void>;
}

/**
 * Custom React hook for fetching EIC new connection fees
 * @param options - Configuration options for the hook
 * @returns Object containing data, loading state, error state, and control functions
 */
export function useNewConnectionFees(
  options: UseNewConnectionFeesOptions = {}
): UseNewConnectionFeesResult {
  const { autoFetch = false, requestId, onSuccess, onError } = options;

  const [data, setData] = useState<NewConnectionFeesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastRequestId, setLastRequestId] = useState<string | null>(null);

  const fetchFees = useCallback(
    async (_requestId: string) => {
      if (!_requestId) {
        const validationError = new Error("Request ID is required");
        setError(validationError);
        onError?.(validationError);
        return;
      }

      setLoading(true);
      setError(null);
      setLastRequestId(_requestId);

      try {
        const result = await apiClientPublic.get<NewConnectionFeesResponse>(
          `/eic/fees/${_requestId}`
        );
        setData(result.data);
        onSuccess?.(result.data);
      } catch (err) {
        setError(err as Error);
        onError?.(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, onError]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setLastRequestId(null);
  }, []);

  const retry = useCallback(async () => {
    if (lastRequestId) {
      await fetchFees(lastRequestId);
    }
  }, [lastRequestId, fetchFees]);

  // Auto-fetch on mount if enabled and requestId is provided
  useEffect(() => {
    if (autoFetch && requestId) {
      fetchFees(requestId);
    }
  }, [autoFetch, requestId, fetchFees]);

  return {
    data,
    loading,
    error,
    fetchFees,
    reset,
    retry,
  };
}
